'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { AuthUser, AuthResponse, RegisterRequest } from '@shared/types/marketplace'
import { apiClient } from '../lib/api'
import { getAuthAdapter } from '../auth/adapters/factory'
import { OtpSession, VerifyResult } from '../auth/adapters/authAdapter'

interface AuthContextType {
  user: AuthUser | null
  availableRoles: ('vendor' | 'consumer')[]
  sendOTP: (phone: string, verifier?: any) => Promise<OtpSession>
  verifyOTP: (sessionId: string, otp: string) => Promise<VerifyResult>
  login: (sessionId: string) => Promise<void>
  register: (sessionId: string, userData: RegisterRequest) => Promise<void>
  switchRole: (role: 'vendor' | 'consumer') => Promise<void>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [availableRoles, setAvailableRoles] = useState<('vendor' | 'consumer')[]>([])
  const [loading, setLoading] = useState(true)
  
  // Get the auth adapter (Firebase or Local OTP based on config)
  const authAdapter = getAuthAdapter()

  useEffect(() => {
    // Check if user is logged in on app load
    const token = localStorage.getItem('token')
    if (token) {
      // Verify token and get user data
      apiClient.setAuthToken(token)
      fetchUserProfile()
    } else {
      setLoading(false)
    }
  }, [])

  const fetchUserProfile = async () => {
    try {
      const response = await apiClient.get('/auth/me')
      if ((response.data as any).success) {
        const userData = (response.data as any).data
        setUser({
          id: userData.id,
          email: userData.email || undefined,
          name: userData.name,
          phone: userData.phone || '',
          role: userData.role
        })
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      localStorage.removeItem('token')
      apiClient.setAuthToken(null)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const sendOTPToPhone = async (phone: string, verifier?: any): Promise<OtpSession> => {
    return await authAdapter.sendOtp(phone, verifier)
  }

  const verifyOTPCode = async (sessionId: string, otp: string): Promise<VerifyResult> => {
    return await authAdapter.verifyOtp(sessionId, otp)
  }

  const login = async (sessionId: string) => {
    try {
      const response = await apiClient.post('/auth/login', { sessionId })
      if ((response.data as any).success) {
        const { user: userData, token, availableRoles: roles } = (response.data as any).data as AuthResponse
        
        localStorage.setItem('token', token)
        apiClient.setAuthToken(token)
        setUser(userData)
        setAvailableRoles(roles || [])
      } else {
        throw new Error((response.data as any).error || 'Login failed')
      }
    } catch (error: any) {
      console.error('Login error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Login failed'
      throw new Error(errorMessage)
    }
  }

  const register = async (sessionId: string, userData: RegisterRequest) => {
    try {
      const response = await apiClient.post('/auth/register', { 
        sessionId,
        ...userData 
      })
      if ((response.data as any).success) {
        const { user: newUser, token, availableRoles: roles } = (response.data as any).data as AuthResponse
        
        localStorage.setItem('token', token)
        apiClient.setAuthToken(token)
        setUser(newUser)
        setAvailableRoles(roles || [])
      } else {
        throw new Error((response.data as any).error || 'Registration failed')
      }
    } catch (error: any) {
      console.error('Registration error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Registration failed'
      throw new Error(errorMessage)
    }
  }

  const switchRole = async (role: 'vendor' | 'consumer') => {
    try {
      const response = await apiClient.post('/auth/switch-role', { role })
      if ((response.data as any).success) {
        const { user: userData, token, availableRoles: roles } = (response.data as any).data as AuthResponse
        
        localStorage.setItem('token', token)
        apiClient.setAuthToken(token)
        setUser(userData)
        setAvailableRoles(roles || [])
      } else {
        throw new Error((response.data as any).error || 'Role switch failed')
      }
    } catch (error: any) {
      console.error('Role switch error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Role switch failed'
      throw new Error(errorMessage)
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    apiClient.setAuthToken(null)
    setUser(null)
    setAvailableRoles([])
  }

  const value: AuthContextType = {
    user,
    availableRoles,
    sendOTP: sendOTPToPhone,
    verifyOTP: verifyOTPCode,
    login,
    register,
    switchRole,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
